import { useCallback, useMemo, useState } from 'react';

import { useAuth } from '@/nucleo/auth/useAuth';
import {
  colunasVisiveisPorPadrao,
  mesmaLista,
  ordemPadrao,
  resolverEstadoDaTabela,
  travarColunasEstruturais,
  type EstadoResolvido,
  type LayoutDaTabela,
  type TableViewColumn,
} from '@/ui/table-view/tabelaVisoes';

/**
 * Preferencia de colunas de uma listagem, guardada NO NAVEGADOR.
 *
 * POR QUE LOCAL, E NAO NO SERVIDOR. Guardar layout de tabela no banco exigiria
 * tabela nova, politica de acesso e rota de API — e o CLAUDE.md e claro: tabela
 * cadastral nasce com politica escrita na mesma migracao, e cada modulo entrega a sua
 * junto com a sua tela. Layout de coluna nao e dado do TEG: e conforto de quem opera,
 * nao muda decisao nenhuma e nao precisa ser auditado. Quando existir "visao
 * compartilhada" — layout que uma pessoa publica para a equipe — ai sim vira dado, com
 * tabela e dono; ate lá, `localStorage` entrega o valor inteiro sem divida nenhuma.
 *
 * O QUE ISSO CUSTA, dito antes de alguem descobrir: a preferencia nao viaja entre
 * navegadores nem sobrevive a limpar dados do site. E aceitavel porque o padrao e
 * sempre util — quem nunca personalizou ve a tabela completa — e reconstruir a
 * personalizacao leva dois cliques.
 *
 * NENHUMA ESCRITA PODE DERRUBAR A TELA. `localStorage` lanca em modo privado de
 * alguns navegadores e quando a cota estoura, e ninguem perde o trabalho porque a
 * largura de uma coluna nao pudesse ser gravada. Toda leitura e escrita e cercada.
 *
 * A CHAVE LEVA O ID DO USUARIO, NAO SO O `contextKey`. `localStorage` e por
 * ORIGEM, nao por sessao: numa maquina compartilhada (comum em posto de
 * atendimento do DTP), sem o id quem abre a tela depois herda o layout de quem
 * usou antes — colunas ocultas por um papel aparecendo, ou nao, para o proximo.
 * Sem usuario autenticado (nao deveria acontecer, a rota e protegida) a
 * preferencia so vive no estado em memoria da sessao: nunca cai num balde
 * compartilhado.
 */

const PREFIXO = 'teg:tabela:';

function chaveDoArmazenamento(contextKey: string, usuarioId: string): string {
  return `${PREFIXO}${usuarioId}:${contextKey}`;
}

function ler(contextKey: string, usuarioId: string): LayoutDaTabela | null {
  try {
    const bruto = window.localStorage.getItem(chaveDoArmazenamento(contextKey, usuarioId));
    if (!bruto) {
      return null;
    }
    const analisado: unknown = JSON.parse(bruto);
    if (!analisado || typeof analisado !== 'object') {
      return null;
    }
    const { visiveis, ordem } = analisado as Partial<LayoutDaTabela>;
    return {
      visiveis: Array.isArray(visiveis) ? visiveis.filter((v) => typeof v === 'string') : [],
      ordem: Array.isArray(ordem) ? ordem.filter((v) => typeof v === 'string') : [],
    };
  } catch {
    // Conteudo corrompido ou armazenamento indisponivel: cai no padrao, que e util.
    return null;
  }
}

function gravar(contextKey: string, usuarioId: string, layout: LayoutDaTabela | null): void {
  try {
    if (layout) {
      window.localStorage.setItem(chaveDoArmazenamento(contextKey, usuarioId), JSON.stringify(layout));
    } else {
      window.localStorage.removeItem(chaveDoArmazenamento(contextKey, usuarioId));
    }
  } catch {
    // Ver o comentario do topo: preferencia de coluna nao derruba tela.
  }
}

export interface PreferenciasDaTabela<T> extends EstadoResolvido<T> {
  /** As colunas declaradas pela tela, ja com as travas estruturais aplicadas. */
  columns: TableViewColumn<T>[];
  /** `true` quando o layout difere do padrao — e o que revela o botao de restaurar. */
  personalizada: boolean;
  updateVisibleColumns: (visibleColumnIds: string[]) => void;
  updateColumnOrder: (columnOrder: string[]) => void;
  resetColumns: () => void;
}

interface Parametros<T> {
  /**
   * Identificador da listagem, no formato `entidade:list` (ex.: `credenciados:list`).
   * Junto com o id do usuario autenticado, forma a chave da preferencia: mudar isso
   * equivale a esquecer o layout de todo mundo.
   */
  contextKey: string;
  columns: readonly TableViewColumn<T>[];
  /** Filtros ativos, para descobrir coluna oculta que explica um recorte em vigor. */
  filterValues?: Record<string, string>;
}

export function useTableViewPreferences<T>({
  contextKey,
  columns: colunasDeclaradas,
  filterValues,
}: Parametros<T>): PreferenciasDaTabela<T> {
  const { usuario } = useAuth();
  const usuarioId = usuario?.id ?? null;

  const columns = useMemo(
    () => travarColunasEstruturais(colunasDeclaradas),
    [colunasDeclaradas],
  );

  /**
   * Estado inicial vem do armazenamento numa funcao, nao numa expressao: ler
   * `localStorage` a cada render e trabalho sincrono repetido a troco de nada, e
   * numa tabela que rerenderiza a cada tecla digitada na busca isso aparece.
   *
   * Sem `usuarioId` (rota protegida sem sessao resolvida ainda) comeca do padrao —
   * nunca lendo o balde de outro usuario.
   */
  const [layout, setLayout] = useState<LayoutDaTabela | null>(() =>
    usuarioId ? ler(contextKey, usuarioId) : null,
  );

  const resolvido = useMemo(
    () => resolverEstadoDaTabela(columns, layout, filterValues),
    [columns, layout, filterValues],
  );

  const aplicar = useCallback(
    (proximo: LayoutDaTabela | null) => {
      if (usuarioId) {
        gravar(contextKey, usuarioId, proximo);
      }
      setLayout(proximo);
    },
    [contextKey, usuarioId],
  );

  const updateVisibleColumns = useCallback(
    (visibleColumnIds: string[]) => {
      aplicar({
        visiveis: visibleColumnIds,
        ordem: layout?.ordem ?? resolvido.columnOrder,
      });
    },
    [aplicar, layout?.ordem, resolvido.columnOrder],
  );

  const updateColumnOrder = useCallback(
    (columnOrder: string[]) => {
      aplicar({
        visiveis: layout?.visiveis ?? resolvido.visibleColumnIds,
        ordem: columnOrder,
      });
    },
    [aplicar, layout?.visiveis, resolvido.visibleColumnIds],
  );

  const resetColumns = useCallback(() => {
    // Apaga em vez de gravar o padrao: assim a tabela volta a acompanhar mudanca de
    // coluna no codigo, em vez de congelar o padrao de hoje para sempre.
    aplicar(null);
  }, [aplicar]);

  const personalizada =
    !!layout &&
    (!mesmaLista(resolvido.visibleColumnIds, colunasVisiveisPorPadrao(columns)) ||
      !mesmaLista(resolvido.columnOrder, ordemPadrao(columns)));

  return {
    ...resolvido,
    columns,
    personalizada,
    updateVisibleColumns,
    updateColumnOrder,
    resetColumns,
  };
}
